// Returns breakdown cards of the «Борлуулалтын аналитик» tab. Same endpoints as
// /returns/dashboard, but drawn with this tab's own pieces (AnalyticsCard,
// HorizontalBarList, RankList, EmptyRow) so they read like every other card on
// the page: one card shell, one bar style, one empty state, no rainbow donut.
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { ReturnAnalyticsApi } from "../../../../apis"
import type { BreakdownDimension, IBreakdownBucket } from "../../../../apis/return-analytics"
import { reasonLabel } from "../../../../components/return/atoms"
import { currencyFormat } from "../../../../utils"
import { RankList } from "../../parts/RankList"
import { AnalyticsCard, EmptyRow } from "../cards/AnalyticsCard"
import { HBarItem, HorizontalBarList } from "../cards/HorizontalBarList"

type RangeProps = { dateFromIso: string; dateToIso: string }

function useBreakdown(dimension: BreakdownDimension, { dateFromIso, dateToIso }: RangeProps) {
  return useSWR(
    ["returns.breakdown." + dimension, dateFromIso, dateToIso],
    () => ReturnAnalyticsApi.breakdown({ dimension, dateFrom: dateFromIso, dateTo: dateToIso }),
    { revalidateOnFocus: false },
  )
}

/** Count rows → bar items with their share of the total. */
function toCountItems(rows: { id: string; label: string; count: number }[]): { items: HBarItem[]; total: number } {
  const total = rows.reduce((s, r) => s + r.count, 0)
  return {
    total,
    items: rows.map((r) => ({
      id: r.id,
      label: r.label,
      amount: r.count,
      percent: total > 0 ? Math.round((r.count / total) * 100) : 0,
    })),
  }
}

function CountBars({ title, helpKey, rows, isLoading }: {
  title: string
  helpKey?: string
  rows: { id: string; label: string; count: number }[]
  isLoading: boolean
}) {
  const { t } = useTranslation(["returns"])
  const { items, total } = toCountItems(rows)
  return (
    <HorizontalBarList
      title={title}
      helpKey={helpKey}
      subtitle={total > 0 ? t("returns:dashboard.chartLabel.returns", { count: total }) : undefined}
      items={items}
      isLoading={isLoading}
      formatValue={(it) => String(it.amount)}
    />
  )
}

const bucketRows = (buckets: IBreakdownBucket[] | undefined, label: (b: string) => string) =>
  (buckets ?? []).map((b, i) => ({ id: b.bucket ?? `none-${i}`, label: b.bucket ? label(b.bucket) : "—", count: b.count }))

export function ReturnReasonCard(props: RangeProps) {
  const { t } = useTranslation(["returns"])
  const { data, isLoading } = useBreakdown("reason", props)
  return (
    <CountBars
      title={t("returns:dashboard.reasonBreakdown.title")}
      helpKey="order.returns-dashboard.section.reason-breakdown"
      rows={bucketRows(data?.buckets, reasonLabel)}
      isLoading={isLoading}
    />
  )
}

export function ReturnRefundChannelCard(props: RangeProps) {
  const { t } = useTranslation(["returns"])
  const { data, isLoading } = useBreakdown("refund-method", props)
  // Returns without a refund channel yet (bucket null / UNSET) are not a channel.
  const buckets = (data?.buckets ?? []).filter((b) => b.bucket && b.bucket !== "UNSET")
  return (
    <CountBars
      title={t("returns:dashboard.refundChannel.title")}
      helpKey="order.returns-dashboard.section.refund-channel"
      rows={bucketRows(buckets, (b) => t(`returns:refundMethod.${b}`, { defaultValue: "—" }))}
      isLoading={isLoading}
    />
  )
}

export function ReturnResolutionTypeCard(props: RangeProps) {
  const { t } = useTranslation(["returns"])
  const { data, isLoading } = useBreakdown("resolution", props)
  // Not decided yet (bucket null / UNSET) is not a resolution type; the raw code used to show up as a slice.
  const buckets = (data?.buckets ?? []).filter((b) => b.bucket && b.bucket !== "UNSET")
  return (
    <CountBars
      title={t("returns:dashboard.resolutionBreakdown.title")}
      rows={bucketRows(buckets, (b) => t(`returns:resolutionType.${b}`, { defaultValue: b }))}
      isLoading={isLoading}
    />
  )
}

export function ReturnResolutionTimeCard({ dateFromIso, dateToIso }: RangeProps) {
  const { t } = useTranslation(["returns"])
  const { data, isLoading } = useSWR(
    ["returns.resolution-histogram", dateFromIso, dateToIso],
    () => ReturnAnalyticsApi.resolutionHistogram({ dateFrom: dateFromIso, dateTo: dateToIso }),
    { revalidateOnFocus: false },
  )
  const buckets = data?.buckets ?? []
  const rows = buckets.map((b) => ({ id: b.key, label: b.label, count: b.count }))
  // All-zero buckets are "nothing resolved yet", not a chart of zeros.
  const hasAny = rows.some((r) => r.count > 0)
  return (
    <CountBars
      title={t("returns:dashboard.resolutionTime.title")}
      rows={hasAny ? rows : []}
      isLoading={isLoading}
    />
  )
}

export function RepeatReturnersCard({ dateFromIso, dateToIso }: RangeProps) {
  const { t } = useTranslation(["returns", "dashboard"])
  const { data, isLoading } = useSWR(
    ["returns.repeat-returners", dateFromIso, dateToIso],
    () => ReturnAnalyticsApi.repeatReturners({ dateFrom: dateFromIso, dateTo: dateToIso, limit: 8 }),
    { revalidateOnFocus: false },
  )
  const buyers = data?.buyers ?? []
  return (
    <AnalyticsCard
      title={t("returns:dashboard.repeatReturners.title")}
      subtitle={data?.threshold ? t("returns:dashboard.repeatReturners.threshold", { count: data.threshold }) : undefined}
      isLoading={isLoading}
    >
      {buyers.length === 0 ? (
        isLoading ? null : <EmptyRow />
      ) : (
        <RankList
          items={buyers.map((b) => ({
            key: b.buyerBusinessId,
            name: b.buyerBusinessName ?? "—",
            count: b.returnCount,
            value: `${t("dashboard:returnsWidget.times", { count: b.returnCount })} · ${currencyFormat(b.totalRefundAmount)}`,
          }))}
        />
      )}
    </AnalyticsCard>
  )
}
